// AcceptInvite.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase/config';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from './firebase/config';

const a = {
  validating: 'Επαλήθευση πρόσκλησης…',
  errorTitle: 'Σφάλμα πρόσκλησης',
  goToLogin: 'Μετάβαση στη σύνδεση',
  joinTitle: (company) => `Εγγραφή στη ${company}`,
  invitedBy: (name, role) => `${name} σάς προσκάλεσε ως ${role}`,
  invitedNoName: (role) => `Έχετε προσκληθεί ως ${role}`,
  emailLabel: 'Email',
  emailHint: 'Αυτό είναι το email στο οποίο στάλθηκε η πρόσκληση',
  nameLabel: 'Το όνομά σας',
  namePlaceholder: 'Εισάγετε το πλήρες όνομά σας',
  passwordLabel: 'Επιλογή κωδικού',
  passwordPlaceholder: 'Τουλάχιστον 8 χαρακτήρες',
  confirmPasswordLabel: 'Επανάληψη κωδικού',
  confirmPasswordPlaceholder: 'Επανάληψη κωδικού',
  submitProcessing: 'Επεξεργασία…',
  submitAccept: 'Αποδοχή & Εγγραφή',
  errAllRequired: 'Όλα τα πεδία είναι υποχρεωτικά.',
  errPasswordShort: 'Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες.',
  errPasswordMismatch: 'Οι κωδικοί δεν ταιριάζουν.',
  errFailed: 'Δεν ήταν δυνατή η επεξεργασία του αιτήματός σας. Δοκιμάστε ξανά.',
  errInvalidLink: 'Μη έγκυρος σύνδεσμος πρόσκλησης.',
  errValidation: 'Δεν ήταν δυνατή η επαλήθευση της πρόσκλησης. Δοκιμάστε ξανά.',
};

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [invitationData, setInvitationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [processingAccept, setProcessingAccept] = useState(false);
  const [headerInfo, setHeaderInfo] = useState({ companyName: '', inviterName: '', companyLogoUrl: '' });

  useEffect(() => {
    const validateToken = async () => {
      if (!token) { setError(a.errInvalidLink); setLoading(false); return; }
      try {
        const validateInvitationToken = httpsCallable(functions, 'validateInvitationToken');
        const result = await validateInvitationToken({ token });
        if (result.data.valid) {
          setInvitationData(result.data);
          setHeaderInfo({
            companyName: result.data.companyName || '',
            inviterName: result.data.inviterName || '',
            companyLogoUrl: result.data.companyLogoUrl || ''
          });
          if (result.data.name) setName(result.data.name);
        } else {
          setError(result.data.error || a.errInvalidLink);
        }
      } catch {
        setError(a.errValidation);
      } finally {
        setLoading(false);
      }
    };
    validateToken();
  }, [token]);

  const handleAcceptInvitation = async (e) => {
    e.preventDefault();
    if (!password || !confirmPassword || !name) { setError(a.errAllRequired); return; }
    if (password.length < 8) { setError(a.errPasswordShort); return; }
    if (password !== confirmPassword) { setError(a.errPasswordMismatch); return; }
    setProcessingAccept(true);
    setError(null);
    try {
      const acceptInvitation = httpsCallable(functions, 'acceptInvitation');
      const result = await acceptInvitation({ token, password, name });
      if (result.data.success) {
        try {
          await signInWithEmailAndPassword(auth, invitationData.email, password);
          navigate('/');
        } catch {
          navigate('/login');
        }
      } else {
        setError(result.data.error || a.errFailed);
      }
    } catch {
      setError(a.errFailed);
    } finally {
      setProcessingAccept(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex items-center space-x-2">
          <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span>{a.validating}</span>
        </div>
      </div>
    );
  }

  if (error && !invitationData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{a.errorTitle}</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button onClick={() => navigate('/login')} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">{a.goToLogin}</button>
        </div>
      </div>
    );
  }

  const company = headerInfo.companyName || invitationData?.companyName || '';
  const inviterText = headerInfo.inviterName
    ? a.invitedBy(headerInfo.inviterName, invitationData?.role || '')
    : a.invitedNoName(invitationData?.role || '');

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="px-6 py-6 text-center bg-gradient-to-r from-blue-600 to-indigo-600">
          {headerInfo.companyLogoUrl ? (
            <img src={headerInfo.companyLogoUrl} alt="logo" className="h-12 w-12 rounded-full mx-auto mb-3 object-cover bg-white" />
          ) : (
            <img src={process.env.PUBLIC_URL + '/logo/logo.png'} alt="InvoiceBox" className="h-14 sm:h-16 w-auto mx-auto mb-3" />
          )}
          <h2 className="text-2xl font-bold text-white">{a.joinTitle(company)}</h2>
          <p className="text-blue-100 mt-2">{inviterText}</p>
        </div>
        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 text-sm text-red-700">{error}</div>
          )}
          <form onSubmit={handleAcceptInvitation} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{a.emailLabel}</label>
              <input type="email" className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" value={invitationData?.email || ''} disabled />
              <p className="mt-1 text-xs text-gray-500">{a.emailHint}</p>
            </div>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">{a.nameLabel}</label>
              <input id="name" type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={name} onChange={(e) => setName(e.target.value)} placeholder={a.namePlaceholder} required />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">{a.passwordLabel}</label>
              <input id="password" type="password" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={a.passwordPlaceholder} required minLength={8} />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">{a.confirmPasswordLabel}</label>
              <input id="confirmPassword" type="password" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder={a.confirmPasswordPlaceholder} required />
            </div>
            <button type="submit" disabled={processingAccept} className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {processingAccept ? (
                <><svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>{a.submitProcessing}</>
              ) : a.submitAccept}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
